import { PaymentMethodType } from './types';

export enum DueDateTypeEnum {
  SubscriptionPayment3BusinessDaysBeforeDueDate = 'subscriptionPayment3BusinessDaysBeforeDueDate',
  OneWeekAdvance = 'oneWeekAdvance',
  TwoWeekAdvance = 'twoWeekAdvance',
  ThreeWeekAdvance = 'threeWeekAdvance',
}

/**
 * Convert a number to its word representation
 * @param num - The number to convert
 * @returns The word representation of the number
 */
export const numberToWord = (num: number): string => {
  const numberWords: Record<number, string> = {
    1: 'one',
    2: 'two',
    3: 'three',
    4: 'four',
    5: 'five',
    6: 'six',
    7: 'seven',
    8: 'eight',
    9: 'nine',
    10: 'ten',
  };
  
  return numberWords[num] || num.toString();
};

/**
 * Get the number of business days a payment must be submitted before the due date.
 * Prefers the value computed server-side (`businessDaysBeforeDueDate` on the selected
 * payment option: 3 for card, 4 for PAD linked via Plaid, 6 for manually-verified PAD).
 * Falls back to a payment-method-based default when that value isn't available yet.
 * @param businessDaysBeforeDueDate - Value from the selected payment option, if known
 * @param paymentMethod - The payment method enum value (fallback only)
 */
export const getBusinessDays = (
  businessDaysBeforeDueDate?: number | null,
  paymentMethod?: PaymentMethodType | null
): number => {
  if (typeof businessDaysBeforeDueDate === 'number') return businessDaysBeforeDueDate;
  return paymentMethod === PaymentMethodType.PreAuthorizedDebit ? 6 : 3;
};

/**
 * Business days after payment date until the payment is marked as success —
 * always one business day less than the pre-due-date submission window.
 */
export const getPaymentProcessingDays = (
  businessDaysBeforeDueDate?: number | null,
  paymentMethod?: PaymentMethodType | null
): number => {
  return getBusinessDays(businessDaysBeforeDueDate, paymentMethod) - 1;
};

export const getAdvancePaymentText = (
  dueDateType?: string | DueDateTypeEnum,
  businessDaysBeforeDueDate?: number | null,
  paymentMethod?: PaymentMethodType | null
) => {
  const businessDaysCount = getBusinessDays(businessDaysBeforeDueDate, paymentMethod);
  const businessDays = `${businessDaysCount} business days`;
  
  switch (dueDateType) {
    case DueDateTypeEnum.OneWeekAdvance:
      return `1 week + ${businessDays}`;
    case DueDateTypeEnum.TwoWeekAdvance:
      return `2 weeks + ${businessDays}`;
    case DueDateTypeEnum.ThreeWeekAdvance:
      return `3 weeks + ${businessDays}`;
    case DueDateTypeEnum.SubscriptionPayment3BusinessDaysBeforeDueDate:
      return businessDays;
    default:
      return businessDays;
  }
};
